// ============================================================
// ByOurConsent.com -- Chapter Page
// /chapters/:slug  -- public + member area
// ============================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Nav    from '../components/Nav';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import api   from '../utils/api';
import toast from 'react-hot-toast';

const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '';

const fmtDateTime = (d) => d
    ? new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
    : '';

const TypeBadge = ({ type }) => {
    const map = {
        announcement: { label: 'Announcement', color: '#1e2a3a', bg: '#e8ecf0' },
        event:        { label: 'Event',         color: '#3b6d11', bg: '#e8f5e0' },
        action:       { label: 'Action',        color: '#8b1a1a', bg: '#f5e8e8' },
        article:      { label: 'Article',       color: '#8b6a3a', bg: '#f5f0e8' }
    };
    const s = map[type] || map.announcement;
    return (
        <span style={{ fontSize: '9px', fontFamily: 'sans-serif', letterSpacing: '0.12em', textTransform: 'uppercase', background: s.bg, color: s.color, padding: '2px 7px', borderRadius: '20px', fontWeight: 600 }}>
            {s.label}
        </span>
    );
};

// ============================================================
// Post form -- all types including article upload
// ============================================================
const PostForm = ({ chapterId, onPosted }) => {
    const fileRef = useRef(null);
    const [type,       setType]       = useState('announcement');
    const [title,      setTitle]      = useState('');
    const [body,       setBody]       = useState('');
    const [eventDate,  setEventDate]  = useState('');
    const [eventLink,  setEventLink]  = useState('');
    const [eventLoc,   setEventLoc]   = useState('');
    const [actionUrl,  setActionUrl]  = useState('');
    const [articleHtml,setArticleHtml]= useState('');
    const [articleFile,setArticleFile]= useState('');
    const [uploading,  setUploading]  = useState(false);
    const [saving,     setSaving]     = useState(false);

    const iStyle = { width:'100%', background:'#f5f0e8', border:'0.5px solid #c8beaa', padding:'8px 10px', fontSize:'13px', fontFamily:'sans-serif', color:'#1e2a3a', borderRadius:'3px', outline:'none', boxSizing:'border-box' };
    const lStyle = { display:'block', fontSize:'10px', fontFamily:'sans-serif', color:'#6a7080', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:'4px' };

    const reset = () => {
        setType('announcement'); setTitle(''); setBody('');
        setEventDate(''); setEventLink(''); setEventLoc('');
        setActionUrl(''); setArticleHtml(''); setArticleFile('');
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        const fd = new FormData();
        fd.append('file', file);
        try {
            const r = await api.post('/articles/upload', fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setArticleHtml(r.data.html);
            setArticleFile(file.name);
            if (r.data.suggested_title && !title) setTitle(r.data.suggested_title);
            toast.success('Document converted!');
        } catch { toast.error('Failed to convert document'); }
        finally { setUploading(false); }
    };

    const postToChapter = async () => {
        if (!title) { toast.error('Title required'); return; }
        if (type === 'article' && !articleHtml) { toast.error('Please upload a document first'); return; }
        setSaving(true);
        try {
            // For article type, first create the article as chapter-only
            let articleId = null;
            if (type === 'article') {
                const r = await api.post('/articles/submit', {
                    title, body_html: articleHtml, excerpt: body,
                    publish_immediately: false,
                    chapter_only: true
                });
                articleId = r.data.article?.id;
            }

            await api.post(`/chapters/${chapterId}/content`, {
                type, title, body,
                event_date: eventDate || null,
                event_link: eventLink || null,
                event_location: eventLoc || null,
                action_url: actionUrl || null,
                article_id: articleId || null,
                article_html: type === 'article' ? articleHtml : null
            });
            toast.success('Posted to chapter!');
            reset();
            onPosted();
        } catch { toast.error('Failed to post'); }
        finally { setSaving(false); }
    };

    const submitToMainSite = async () => {
        if (!title) { toast.error('Title required'); return; }
        if (!articleHtml) { toast.error('Please upload a document first'); return; }
        setSaving(true);
        try {
            await api.post('/articles/submit', {
                title, body_html: articleHtml, excerpt: body,
                publish_immediately: false
            });
            toast.success('Article submitted for review on main site!');
            reset();
        } catch { toast.error('Submission failed'); }
        finally { setSaving(false); }
    };

    return (
        <div style={{ background:'#f5f0e8', border:'0.5px solid #c8beaa', borderRadius:'4px', padding:'1.25rem', marginBottom:'1.5rem' }}>
            <div style={{ fontSize:'15px', fontWeight:600, color:'#1e2a3a', fontFamily:"'EB Garamond', Georgia, serif", marginBottom:'1rem', paddingBottom:'8px', borderBottom:'0.5px solid #c8beaa' }}>
                Post to chapter
            </div>

            {/* Type selector */}
            <div style={{ display:'flex', gap:'8px', marginBottom:'1rem', flexWrap:'wrap' }}>
                {[
                    { value:'announcement', label:'📢 Announcement' },
                    { value:'event',        label:'📅 Event' },
                    { value:'action',       label:'⚡ Action item' },
                    { value:'article',      label:'📄 Article' }
                ].map(t => (
                    <button key={t.value} onClick={() => setType(t.value)}
                        style={{ background: type===t.value ? '#1e2a3a' : 'transparent', color: type===t.value ? '#e8dfc8' : '#4a5060', border:`0.5px solid ${type===t.value ? '#1e2a3a' : '#c8beaa'}`, padding:'5px 12px', fontSize:'12px', fontFamily:'sans-serif', borderRadius:'20px', cursor:'pointer' }}>
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Title */}
            <div style={{ marginBottom:'10px' }}>
                <label style={lStyle}>Title *</label>
                <input style={iStyle} value={title} onChange={e => setTitle(e.target.value)} placeholder="Post title" />
            </div>

            {/* Body */}
            <div style={{ marginBottom:'10px' }}>
                <label style={lStyle}>{type === 'article' ? 'Excerpt / summary' : 'Details'}</label>
                <textarea style={{ ...iStyle, resize:'vertical' }} value={body} onChange={e => setBody(e.target.value)} rows={3} placeholder={type === 'article' ? 'Brief summary for the feed...' : 'Add details...'} />
            </div>

            {/* Event fields */}
            {type === 'event' && (
                <>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'10px' }}>
                        <div><label style={lStyle}>Date & time</label><input style={iStyle} type="datetime-local" value={eventDate} onChange={e => setEventDate(e.target.value)} /></div>
                        <div><label style={lStyle}>Zoom / meeting link</label><input style={iStyle} value={eventLink} onChange={e => setEventLink(e.target.value)} placeholder="https://zoom.us/..." /></div>
                    </div>
                    <div style={{ marginBottom:'10px' }}><label style={lStyle}>Location</label><input style={iStyle} value={eventLoc} onChange={e => setEventLoc(e.target.value)} placeholder="Address or 'Online'" /></div>
                </>
            )}

            {/* Action fields */}
            {type === 'action' && (
                <div style={{ marginBottom:'10px' }}>
                    <label style={lStyle}>Action URL</label>
                    <input style={iStyle} value={actionUrl} onChange={e => setActionUrl(e.target.value)} placeholder="Link to bill, petition, contact form..." />
                </div>
            )}

            {/* Article upload */}
            {type === 'article' && (
                <div style={{ marginBottom:'10px', padding:'12px', background:'#ede8dc', border:'0.5px solid #c8beaa', borderRadius:'3px' }}>
                    <label style={lStyle}>Upload document (.docx, .doc, .txt)</label>
                    {articleHtml ? (
                        <div style={{ display:'flex', alignItems:'center', gap:'10px', marginTop:'4px' }}>
                            <span style={{ fontSize:'12px', fontFamily:'sans-serif', color:'#3b6d11' }}>✓ {articleFile} converted</span>
                            <button onClick={() => { setArticleHtml(''); setArticleFile(''); }}
                                style={{ background:'transparent', border:'none', color:'#8b1a1a', fontSize:'11px', fontFamily:'sans-serif', cursor:'pointer', textDecoration:'underline' }}>
                                Remove
                            </button>
                        </div>
                    ) : (
                        <div style={{ marginTop:'4px' }}>
                            <button onClick={() => fileRef.current?.click()}
                                style={{ background:'transparent', border:'0.5px solid #c8beaa', padding:'6px 14px', fontSize:'12px', fontFamily:'sans-serif', color:'#4a5060', cursor:'pointer', borderRadius:'3px' }}>
                                {uploading ? 'Converting...' : 'Choose file'}
                            </button>
                            <span style={{ fontSize:'11px', fontFamily:'sans-serif', color:'#8a8070', marginLeft:'10px' }}>Max 10MB</span>
                        </div>
                    )}
                    <input ref={fileRef} type="file" accept=".docx,.doc,.txt,.rtf" onChange={handleFileUpload} style={{ display:'none' }} />
                </div>
            )}

            {/* Action buttons */}
            <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end', marginTop:'10px', flexWrap:'wrap' }}>
                {/* Article gets two buttons */}
                {type === 'article' && articleHtml && (
                    <button onClick={submitToMainSite} disabled={saving}
                        style={{ background:'transparent', border:'0.5px solid #1e2a3a', padding:'8px 14px', fontSize:'11px', fontFamily:'sans-serif', color:'#1e2a3a', cursor:'pointer', borderRadius:'3px' }}>
                        Submit to main site for review
                    </button>
                )}
                <button onClick={postToChapter} disabled={saving || (type === 'article' && !articleHtml)}
                    style={{ background:'#8b1a1a', color:'#f5f0e8', border:'none', padding:'8px 20px', fontSize:'12px', fontFamily:'sans-serif', letterSpacing:'0.06em', textTransform:'uppercase', cursor:'pointer', borderRadius:'3px', opacity: (saving || (type==='article' && !articleHtml)) ? 0.6 : 1 }}>
                    {saving ? 'Posting...' : type === 'article' ? 'Post to chapter only →' : 'Post →'}
                </button>
            </div>
        </div>
    );
};

// ============================================================
// Main ChapterPage
// ============================================================
const ChapterPage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { user, isAdmin } = useAuth();

    const [chapter,    setChapter]    = useState(null);
    const [membership, setMembership] = useState(null);
    const [content,    setContent]    = useState([]);
    const [members,    setMembers]    = useState([]);
    const [pending,    setPending]    = useState([]);
    const [loading,    setLoading]    = useState(true);
    const [requesting, setRequesting] = useState(false);
    const [activeTab,  setActiveTab]  = useState('feed');
    const [threads,    setThreads]    = useState([]);
    const [newThread,  setNewThread]  = useState({ title: '', body: '' });
    const [postingThread, setPostingThread] = useState(false);

    const isLeader = chapter?.leader_id === user?.id;
    const isMember = membership?.status === 'approved';
    const hasAccess = isMember || isLeader || isAdmin;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get(`/chapters/${slug}`);
            setChapter(r.data.chapter);
            setMembership(r.data.memberStatus);
        } catch {
            toast.error('Chapter not found');
            navigate('/chapters');
        } finally { setLoading(false); }
    }, [slug, navigate]);

    useEffect(() => { load(); }, [load]);

    const loadContent = useCallback(async () => {
        if (!chapter || !hasAccess) return;
        try {
            const r = await api.get(`/chapters/${chapter.id}/content`);
            setContent(r.data.content || []);
        } catch { }
    }, [chapter, hasAccess]);

    const loadThreads = useCallback(async () => {
        if (!chapter || !hasAccess) return;
        try {
            const r = await api.get(`/forum/chapter/${chapter.id}`);
            setThreads(r.data.threads || []);
        } catch { }
    }, [chapter, hasAccess]);

    const loadMembers = useCallback(async () => {
        if (!chapter || (!isLeader && !isAdmin)) return;
        try {
            const [approved, pend] = await Promise.all([
                api.get(`/chapters/${chapter.id}/members?status=approved`),
                api.get(`/chapters/${chapter.id}/members?status=pending`)
            ]);
            setMembers(approved.data.members || []);
            setPending(pend.data.members || []);
        } catch { }
    }, [chapter, isLeader, isAdmin]);

    useEffect(() => {
        if (chapter && hasAccess) { loadContent(); loadThreads(); loadMembers(); }
    }, [chapter, hasAccess, loadContent, loadThreads, loadMembers]);

    const requestMembership = async () => {
        if (!user) {
            // New user — send to register, return to this chapter after
            navigate('/register', { state: { from: { pathname: `/chapters/${slug}` } } });
            return;
        }
        setRequesting(true);
        try {
            await api.post(`/chapters/${chapter.id}/request`);
            toast.success('Membership request sent! The chapter leader will review your request.');
            load();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to send request');
        } finally { setRequesting(false); }
    };

    const handleMember = async (userId, action) => {
        try {
            await api.put(`/chapters/${chapter.id}/members/${userId}`, { action });
            toast.success(action === 'approve' ? 'Member approved!' : 'Member denied');
            loadMembers(); load();
        } catch { toast.error('Failed to update member'); }
    };

    if (loading) return (
        <div style={{ minHeight:'100vh', background:'#f5f0e8' }}><Nav />
            <div style={{ padding:'3rem', textAlign:'center', fontFamily:'sans-serif', color:'#8a8070' }}>Loading...</div>
        </div>
    );
    if (!chapter) return null;

    const tabs = [
        { key: 'feed',       label: 'Feed' },
        { key: 'discussion', label: 'Discussion' },
        ...(isLeader || isAdmin ? [{ key: 'members', label: `Members${pending.length > 0 ? ` (${pending.length} pending)` : ''}` }] : [])
    ];

    return (
        <>
            <Helmet><title>{chapter.name} — By Our Consent</title></Helmet>
            <Nav />

            {/* Chapter header */}
            <div style={{ background:'#1e2a3a', borderBottom:'2px solid #8b1a1a', padding:'2rem 1.5rem' }}>
                <div style={{ maxWidth:'860px', margin:'0 auto' }}>
                    <Link to="/chapters" style={{ fontSize:'11px', fontFamily:'sans-serif', color:'#8a9aaa', textDecoration:'none', letterSpacing:'0.06em', textTransform:'uppercase' }}>
                        ← Chapters
                    </Link>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginTop:'10px', gap:'1rem', flexWrap:'wrap' }}>
                        <div>
                            <div style={{ display:'flex', gap:'8px', alignItems:'center', marginBottom:'6px' }}>
                                <span style={{ fontSize:'10px', fontFamily:'sans-serif', letterSpacing:'0.12em', textTransform:'uppercase', background: chapter.type==='location'?'#e8f0ff':'#f0e8ff', color: chapter.type==='location'?'#2a4a8a':'#4a2a8a', padding:'2px 8px', borderRadius:'20px', fontWeight:600 }}>
                                    {chapter.type === 'location' ? '📍 Location' : '💡 Concept'}
                                </span>
                                {chapter.location && <span style={{ fontSize:'12px', fontFamily:'sans-serif', color:'#8b9a7a' }}>{chapter.location}</span>}
                            </div>
                            <h1 style={{ fontSize:'30px', fontWeight:600, color:'#e8dfc8', fontFamily:"'EB Garamond', Georgia, serif", lineHeight:1.15, marginBottom:'6px' }}>
                                {chapter.name}
                            </h1>
                            <div style={{ fontSize:'13px', fontFamily:'sans-serif', color:'#8a9aaa' }}>
                                {chapter.member_count} members
                                {chapter.leader_first && ` · Led by ${chapter.leader_first} ${chapter.leader_last}`}
                            </div>
                        </div>

                        {/* Status / membership button */}
                        {!hasAccess && !membership && (
                            <button onClick={requestMembership} disabled={requesting}
                                style={{ background:'#8b1a1a', color:'#f5f0e8', border:'none', padding:'10px 24px', fontSize:'12px', fontFamily:'sans-serif', letterSpacing:'0.08em', textTransform:'uppercase', cursor:'pointer', borderRadius:'3px' }}>
                                {requesting ? 'Requesting...' : 'Request membership →'}
                            </button>
                        )}
                        {membership?.status === 'pending' && (
                            <div style={{ background:'rgba(255,255,255,0.1)', padding:'10px 16px', borderRadius:'3px', fontSize:'12px', fontFamily:'sans-serif', color:'#e8dfc8' }}>
                                ⏳ Membership request pending
                            </div>
                        )}
                        {hasAccess && !isLeader && !isAdmin && (
                            <div style={{ background:'rgba(59,109,17,0.3)', padding:'10px 16px', borderRadius:'3px', fontSize:'12px', fontFamily:'sans-serif', color:'#c8f0a8' }}>
                                ✓ Member
                            </div>
                        )}
                        {(isLeader || isAdmin) && (
                            <span style={{ background:'rgba(255,255,255,0.1)', padding:'8px 14px', borderRadius:'3px', fontSize:'11px', fontFamily:'sans-serif', color:'#e8dfc8', letterSpacing:'0.06em', textTransform:'uppercase' }}>
                                {isLeader ? '⭐ Chapter leader' : '🛡 Admin'}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Public info -- not a member */}
            {!hasAccess && (
                <div style={{ maxWidth:'860px', margin:'0 auto', padding:'2rem 1.5rem' }}>
                    <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:'2rem', alignItems:'start' }}>
                        <div>
                            {chapter.description && (
                                <div style={{ marginBottom:'1.5rem' }}>
                                    <div style={{ fontSize:'11px', fontFamily:'sans-serif', letterSpacing:'0.12em', textTransform:'uppercase', color:'#8b6a3a', marginBottom:'8px' }}>About</div>
                                    <p style={{ fontSize:'15px', fontFamily:'sans-serif', color:'#4a5060', lineHeight:1.7 }}>{chapter.description}</p>
                                </div>
                            )}
                            {chapter.purpose && (
                                <div style={{ marginBottom:'1.5rem' }}>
                                    <div style={{ fontSize:'11px', fontFamily:'sans-serif', letterSpacing:'0.12em', textTransform:'uppercase', color:'#8b6a3a', marginBottom:'8px' }}>Purpose</div>
                                    <p style={{ fontSize:'15px', fontFamily:'sans-serif', color:'#4a5060', lineHeight:1.7 }}>{chapter.purpose}</p>
                                </div>
                            )}
                            {chapter.meeting_schedule && (
                                <div style={{ padding:'1rem', background:'#f5f0e8', border:'0.5px solid #c8beaa', borderRadius:'3px' }}>
                                    <div style={{ fontSize:'11px', fontFamily:'sans-serif', letterSpacing:'0.12em', textTransform:'uppercase', color:'#8b6a3a', marginBottom:'6px' }}>Meetings</div>
                                    <div style={{ fontSize:'14px', fontFamily:'sans-serif', color:'#1e2a3a' }}>{chapter.meeting_schedule}</div>
                                    {chapter.meeting_link && (
                                        <a href={chapter.meeting_link} target="_blank" rel="noopener noreferrer"
                                            style={{ display:'inline-block', marginTop:'8px', fontSize:'12px', fontFamily:'sans-serif', color:'#8b1a1a' }}>
                                            Join meeting →
                                        </a>
                                    )}
                                </div>
                            )}
                        </div>

                        <div>
                            <div style={{ background:'linear-gradient(135deg, #1e2a3a 0%, #2e3d52 100%)', border:'0.5px solid #3a4e64', borderLeft:'3px solid #8b1a1a', borderRadius:'4px', padding:'1.25rem', marginBottom:'1rem' }}>
                                <div style={{ fontSize:'9px', fontFamily:'sans-serif', letterSpacing:'0.16em', textTransform:'uppercase', color:'#8b9a7a', marginBottom:'10px' }}>
                                    Chapter leader
                                </div>
                                <div style={{ display:'flex', gap:'10px', alignItems:'flex-start' }}>
                                    {chapter.leader_photo ? (
                                        <img src={chapter.leader_photo.startsWith('http') ? chapter.leader_photo : `http://localhost:5002${chapter.leader_photo}`}
                                            alt={`${chapter.leader_first} ${chapter.leader_last}`}
                                            style={{ width:'48px', height:'48px', borderRadius:'50%', objectFit:'cover', flexShrink:0, border:'2px solid #8b1a1a' }} />
                                    ) : (
                                        <div style={{ width:'48px', height:'48px', borderRadius:'50%', background:'#8b1a1a', display:'flex', alignItems:'center', justifyContent:'center', color:'#f5f0e8', fontSize:'18px', fontFamily:"'EB Garamond', Georgia, serif", fontWeight:600, flexShrink:0 }}>
                                            {chapter.leader_first?.[0]}
                                        </div>
                                    )}
                                    <div>
                                        <div style={{ fontSize:'15px', fontWeight:600, color:'#e8dfc8', fontFamily:"'EB Garamond', Georgia, serif", lineHeight:1.2 }}>
                                            {chapter.leader_first} {chapter.leader_last}
                                        </div>
                                        {chapter.contact_email && (
                                            <a href={`mailto:${chapter.contact_email}`} style={{ fontSize:'11px', fontFamily:'sans-serif', color:'#8b9a7a', display:'block', marginTop:'3px' }}>
                                                {chapter.contact_email}
                                            </a>
                                        )}
                                        <Link to={`/author/${encodeURIComponent((chapter.leader_first + ' ' + chapter.leader_last).trim())}`}
                                            style={{ display:'inline-block', marginTop:'8px', background:'#8b1a1a', color:'#f5f0e8', padding:'4px 10px', fontSize:'10px', fontFamily:'sans-serif', letterSpacing:'0.08em', textTransform:'uppercase', textDecoration:'none', borderRadius:'2px' }}>
                                            View profile →
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            <div style={{ background:'#1e2a3a', borderRadius:'4px', padding:'1.25rem', textAlign:'center' }}>
                                <div style={{ fontSize:'22px', fontWeight:600, color:'#e8dfc8', fontFamily:"'EB Garamond', Georgia, serif" }}>{chapter.member_count}</div>
                                <div style={{ fontSize:'10px', fontFamily:'sans-serif', color:'#8a9aaa', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:'12px' }}>Members</div>
                                <button onClick={requestMembership} disabled={requesting || !!membership}
                                    style={{ width:'100%', background:'#8b1a1a', color:'#f5f0e8', border:'none', padding:'8px', fontSize:'11px', fontFamily:'sans-serif', letterSpacing:'0.08em', textTransform:'uppercase', cursor: membership ? 'default' : 'pointer', borderRadius:'2px' }}>
                                    {membership?.status === 'pending' ? '⏳ Request pending' : 'Request membership'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Member area */}
            {hasAccess && (
                <>
                    {/* Tab bar */}
                    <div style={{ background:'#ede8dc', borderBottom:'0.5px solid #c8beaa' }}>
                        <div style={{ maxWidth:'860px', margin:'0 auto', display:'flex' }}>
                            {tabs.map(t => (
                                <button key={t.key} onClick={() => setActiveTab(t.key)}
                                    style={{ background:'transparent', border:'none', borderBottom: activeTab===t.key ? '2px solid #8b1a1a' : '2px solid transparent', padding:'10px 18px', fontSize:'13px', fontFamily:'sans-serif', color: activeTab===t.key ? '#1e2a3a' : '#6a7080', fontWeight: activeTab===t.key ? 600 : 400, cursor:'pointer', marginBottom:'-1px', whiteSpace:'nowrap' }}>
                                    {t.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div style={{ maxWidth:'860px', margin:'0 auto', padding:'1.5rem' }}>

                        {/* FEED TAB */}
                        {activeTab === 'feed' && (
                            <div>
                                {/* Post form -- leader/admin only */}
                                {(isLeader || isAdmin) && (
                                    <PostForm chapterId={chapter.id} onPosted={loadContent} />
                                )}

                                {content.length === 0 ? (
                                    <div style={{ padding:'2rem', textAlign:'center', background:'#f5f0e8', border:'0.5px solid #c8beaa', borderRadius:'4px', fontFamily:'sans-serif', color:'#8a8070' }}>
                                        No posts yet.{(isLeader || isAdmin) && ' Use the form above to post your first update!'}
                                    </div>
                                ) : (
                                    content.map(c => (
                                        <div key={c.id} style={{ background: c.pinned ? '#fff8f0' : '#f5f0e8', border: `0.5px solid ${c.pinned ? '#d4a96a' : '#c8beaa'}`, borderLeft: c.pinned ? '3px solid #8b6a3a' : '0.5px solid #c8beaa', borderRadius:'4px', padding:'1.25rem', marginBottom:'10px' }}>
                                            <div style={{ display:'flex', gap:'8px', alignItems:'center', marginBottom:'8px' }}>
                                                <TypeBadge type={c.type} />
                                                {c.pinned && <span style={{ fontSize:'9px', fontFamily:'sans-serif', color:'#8b6a3a', letterSpacing:'0.1em', textTransform:'uppercase' }}>📌 Pinned</span>}
                                                <span style={{ fontSize:'11px', fontFamily:'sans-serif', color:'#8a8070', marginLeft:'auto' }}>
                                                    {c.author_first} {c.author_last} · {fmtDate(c.created_at)}
                                                </span>
                                            </div>
                                            <div style={{ fontSize:'17px', fontWeight:600, color:'#1e2a3a', fontFamily:"'EB Garamond', Georgia, serif", marginBottom:'6px' }}>
                                                {c.article_title || c.title}
                                            </div>
                                            {c.body && (
                                                <div style={{ fontSize:'14px', fontFamily:'sans-serif', color:'#4a5060', lineHeight:1.6, marginBottom:'8px' }}>
                                                    {c.body}
                                                </div>
                                            )}
                                            {/* Article body inline */}
                                            {c.article_html && (
                                                <div className="article-body" style={{ maxHeight:'300px', overflowY:'auto', padding:'10px', background:'#ede8dc', borderRadius:'3px', marginBottom:'8px' }}
                                                    dangerouslySetInnerHTML={{ __html: c.article_html }} />
                                            )}
                                            {c.event_date && (
                                                <div style={{ fontSize:'12px', fontFamily:'sans-serif', color:'#3b6d11', marginBottom:'4px' }}>
                                                    📅 {fmtDateTime(c.event_date)}{c.event_location && ` · ${c.event_location}`}
                                                </div>
                                            )}
                                            {c.event_link && (
                                                <a href={c.event_link} target="_blank" rel="noopener noreferrer"
                                                    style={{ fontSize:'12px', fontFamily:'sans-serif', color:'#8b1a1a', textDecoration:'none' }}>
                                                    Join event →
                                                </a>
                                            )}
                                            {c.action_url && (
                                                <a href={c.action_url} target="_blank" rel="noopener noreferrer"
                                                    style={{ display:'inline-block', background:'#8b1a1a', color:'#f5f0e8', padding:'6px 16px', fontSize:'11px', fontFamily:'sans-serif', letterSpacing:'0.06em', textTransform:'uppercase', textDecoration:'none', borderRadius:'2px' }}>
                                                    Take action →
                                                </a>
                                            )}
                                            {c.article_slug && (
                                                <Link to={`/articles/${c.article_slug}`}
                                                    style={{ fontSize:'12px', fontFamily:'sans-serif', color:'#8b1a1a', textDecoration:'none' }}>
                                                    Read on main site →
                                                </Link>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        )}

                        {/* DISCUSSION TAB */}
                        {activeTab === 'discussion' && (
                            <div>
                                {/* New thread form -- leader/admin only on mobile, all members on desktop */}
                                {(isLeader || isAdmin || isMember) && (
                                    <div style={{ background:'#f5f0e8', border:'0.5px solid #c8beaa', borderRadius:'4px', padding:'1.25rem', marginBottom:'1rem' }}>
                                        <div style={{ fontSize:'13px', fontWeight:600, color:'#1e2a3a', fontFamily:"'EB Garamond', Georgia, serif", marginBottom:'10px' }}>
                                            Start a discussion
                                        </div>
                                        <input
                                            style={{ width:'100%', background:'#fff', border:'0.5px solid #c8beaa', padding:'8px 12px', fontSize:'13px', fontFamily:'sans-serif', color:'#1e2a3a', borderRadius:'3px', outline:'none', marginBottom:'8px', boxSizing:'border-box' }}
                                            placeholder="Discussion title..."
                                            value={newThread.title}
                                            onChange={e => setNewThread(p => ({...p, title: e.target.value}))}
                                        />
                                        <textarea
                                            style={{ width:'100%', background:'#fff', border:'0.5px solid #c8beaa', padding:'8px 12px', fontSize:'13px', fontFamily:'sans-serif', color:'#1e2a3a', borderRadius:'3px', outline:'none', resize:'vertical', boxSizing:'border-box' }}
                                            rows={3}
                                            placeholder="What would you like to discuss?..."
                                            value={newThread.body}
                                            onChange={e => setNewThread(p => ({...p, body: e.target.value}))}
                                        />
                                        <button
                                            disabled={!newThread.title.trim() || postingThread}
                                            onClick={async () => {
                                                if (!newThread.title.trim()) return;
                                                setPostingThread(true);
                                                try {
                                                    await api.post('/forum/threads', {
                                                        title: newThread.title,
                                                        body: newThread.body,
                                                        chapter_id: chapter.id
                                                    });
                                                    setNewThread({ title: '', body: '' });
                                                    loadThreads();
                                                } catch { }
                                                setPostingThread(false);
                                            }}
                                            style={{ marginTop:'8px', background:'#8b1a1a', color:'#f5f0e8', border:'none', padding:'8px 18px', fontSize:'12px', fontFamily:'sans-serif', letterSpacing:'0.06em', textTransform:'uppercase', borderRadius:'3px', cursor:'pointer', opacity: (!newThread.title.trim() || postingThread) ? 0.5 : 1 }}>
                                            {postingThread ? 'Posting...' : 'Post discussion'}
                                        </button>
                                    </div>
                                )}

                                {/* Thread list */}
                                {threads.length === 0 ? (
                                    <div style={{ padding:'2rem', textAlign:'center', background:'#f5f0e8', border:'0.5px solid #c8beaa', borderRadius:'4px', fontFamily:'sans-serif', color:'#8a8070' }}>
                                        No discussions yet. {(isLeader || isAdmin || isMember) ? 'Start one above!' : ''}
                                    </div>
                                ) : (
                                    <div>
                                        <div style={{ fontSize:'11px', fontFamily:'sans-serif', color:'#8a8070', marginBottom:'8px', letterSpacing:'0.06em', textTransform:'uppercase' }}>
                                            {threads.length} discussion{threads.length !== 1 ? 's' : ''} · pinned first, then newest
                                        </div>
                                        {threads.map(t => (
                                            <div key={t.id} style={{ background:'#f5f0e8', border:'0.5px solid #c8beaa', borderLeft: t.is_pinned ? '3px solid #8b1a1a' : '0.5px solid #c8beaa', borderRadius:'4px', padding:'8px 12px', marginBottom:'6px', display:'flex', alignItems:'center', gap:'10px' }}>
                                                {/* Thread link -- takes up most space */}
                                                <Link to={`/forum/${t.slug}`} style={{ textDecoration:'none', flex:1, minWidth:0 }}>
                                                    <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                                                        {t.is_pinned && <span style={{ fontSize:'10px', color:'#8b1a1a', flexShrink:0 }}>📌</span>}
                                                        <span style={{ fontSize:'14px', fontWeight:600, color:'#1e2a3a', fontFamily:"'EB Garamond', Georgia, serif", overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>
                                                            {t.title}
                                                        </span>
                                                    </div>
                                                    <div style={{ fontSize:'11px', fontFamily:'sans-serif', color:'#8a8070', marginTop:'2px' }}>
                                                        {t.author_name || (t.author_first + ' ' + t.author_last)} · {fmtDate(t.created_at)} · {t.reply_count} {t.reply_count === 1 ? 'reply' : 'replies'}
                                                    </div>
                                                </Link>

                                                {/* Leader moderation controls */}
                                                {(isLeader || isAdmin) && (
                                                    <div style={{ display:'flex', gap:'4px', flexShrink:0 }}>
                                                        <button
                                                            title={t.is_pinned ? 'Unpin' : 'Pin to top'}
                                                            onClick={async () => {
                                                                try {
                                                                    await api.put(`/forum/threads/${t.id}`, { is_pinned: !t.is_pinned });
                                                                    loadThreads();
                                                                } catch {}
                                                            }}
                                                            style={{ background:'transparent', border:'0.5px solid #c8beaa', borderRadius:'3px', padding:'3px 7px', fontSize:'11px', cursor:'pointer', color: t.is_pinned ? '#8b1a1a' : '#8a8070' }}>
                                                            {t.is_pinned ? 'Unpin' : 'Pin to top'}
                                                        </button>
                                                        <button
                                                            title="Delete thread"
                                                            onClick={async () => {
                                                                if (!window.confirm('Delete this discussion? This cannot be undone.')) return;
                                                                try {
                                                                    await api.delete(`/forum/threads/${t.id}`);
                                                                    loadThreads();
                                                                } catch {}
                                                            }}
                                                            style={{ background:'transparent', border:'0.5px solid #d4a8a0', borderRadius:'3px', padding:'3px 7px', fontSize:'11px', cursor:'pointer', color:'#8b1a1a' }}>
                                                            Delete
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* MEMBERS TAB -- leader/admin only */}
                        {activeTab === 'members' && (isLeader || isAdmin) && (
                            <div>
                                {/* Pending */}
                                {pending.length > 0 && (
                                    <div style={{ marginBottom:'1.5rem' }}>
                                        <div style={{ fontSize:'13px', fontWeight:600, color:'#8b6a3a', fontFamily:'sans-serif', marginBottom:'10px', letterSpacing:'0.06em', textTransform:'uppercase' }}>
                                            Pending requests ({pending.length})
                                        </div>
                                        {pending.map(m => (
                                            <div key={m.id} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'10px', background:'#fff8f0', border:'0.5px solid #d4a96a', borderRadius:'3px', marginBottom:'6px' }}>
                                                <div style={{ flex:1 }}>
                                                    <div style={{ fontSize:'14px', fontWeight:600, color:'#1e2a3a', fontFamily:'sans-serif' }}>{m.first_name} {m.last_name}</div>
                                                    <div style={{ fontSize:'11px', fontFamily:'sans-serif', color:'#8a8070' }}>{m.email} · Requested {fmtDate(m.requested_at)}</div>
                                                </div>
                                                <div style={{ display:'flex', gap:'6px' }}>
                                                    <button onClick={() => handleMember(m.user_id, 'approve')}
                                                        style={{ background:'#3b6d11', color:'#f5f0e8', border:'none', padding:'5px 12px', fontSize:'11px', fontFamily:'sans-serif', cursor:'pointer', borderRadius:'2px' }}>
                                                        Approve
                                                    </button>
                                                    <button onClick={() => handleMember(m.user_id, 'deny')}
                                                        style={{ background:'transparent', border:'0.5px solid #8b1a1a', color:'#8b1a1a', padding:'5px 12px', fontSize:'11px', fontFamily:'sans-serif', cursor:'pointer', borderRadius:'2px' }}>
                                                        Deny
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Approved members */}
                                <div style={{ fontSize:'13px', fontWeight:600, color:'#4a5060', fontFamily:'sans-serif', marginBottom:'10px', letterSpacing:'0.06em', textTransform:'uppercase' }}>
                                    Members ({members.length})
                                </div>
                                {members.length === 0 ? (
                                    <div style={{ padding:'1.5rem', textAlign:'center', fontFamily:'sans-serif', color:'#8a8070', background:'#f5f0e8', border:'0.5px solid #c8beaa', borderRadius:'4px' }}>
                                        No approved members yet
                                    </div>
                                ) : (
                                    members.map((m, i) => (
                                        <div key={m.id} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'10px', background: i%2===0 ? '#f5f0e8' : '#ede8dc', borderBottom:'0.5px solid #c8beaa' }}>
                                            <div style={{ width:'36px', height:'36px', borderRadius:'50%', background:'#1e2a3a', display:'flex', alignItems:'center', justifyContent:'center', color:'#e8dfc8', fontSize:'14px', fontWeight:600, flexShrink:0 }}>
                                                {m.first_name[0]}
                                            </div>
                                            <div style={{ flex:1 }}>
                                                <div style={{ fontSize:'13px', fontWeight:600, color:'#1e2a3a', fontFamily:'sans-serif' }}>
                                                    {m.first_name} {m.last_name}
                                                    {m.role === 'leader' && <span style={{ marginLeft:'6px', fontSize:'9px', background:'#8b1a1a', color:'#f5f0e8', padding:'1px 5px', borderRadius:'2px', letterSpacing:'0.08em', textTransform:'uppercase' }}>Leader</span>}
                                                </div>
                                                <div style={{ fontSize:'11px', fontFamily:'sans-serif', color:'#8a8070' }}>
                                                    {m.email} · Joined {fmtDate(m.approved_at)}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                </>
            )}

            <Footer />
        </>
    );
};

export default ChapterPage;
